import { useState } from "react";
import { useLocation } from "wouter";
import { loginAdminSession } from "@workspace/api-client-react";
import { LogoMark } from "@/components/Logo";
import QuoteCarousel from "@/components/QuoteCarousel";
import { Loader2, ArrowLeft } from "lucide-react";

async function sha256hex(text: string): Promise<string> {
  const encoded = new TextEncoder().encode(text);
  const buf = await crypto.subtle.digest("SHA-256", encoded);
  return Array.from(new Uint8Array(buf))
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
}

export default function AdminGateway() {
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [shake, setShake] = useState(false);
  const [loading, setLoading] = useState(false);
  const [, navigate] = useLocation();

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const hash = await sha256hex(password);
      await loginAdminSession(hash);
      navigate("/admin");
    } catch (err: unknown) {
      const apiErr = err as { status?: number; message?: string };
      const msg =
        apiErr.status === 403
          ? "Incorrect password"
          : (apiErr.message ?? "Something went wrong");
      setError(msg);
      setShake(true);
      setPassword("");
      setTimeout(() => setShake(false), 500);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div
      style={{
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        background: "hsl(var(--background))",
      }}
    >
      <nav style={{ borderBottom: "1px solid hsl(var(--border))", padding: "0 20px", height: "52px", display: "flex", alignItems: "center" }}>
        <button
          onClick={() => navigate("/")}
          style={{
            display: "inline-flex", alignItems: "center", gap: "6px",
            fontSize: "13px", color: "hsl(var(--muted-foreground))",
            background: "none", border: "none", cursor: "pointer", padding: 0,
            fontFamily: "inherit", transition: "color 0.15s",
          }}
          onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.color = "hsl(var(--foreground))"; }}
          onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.color = "hsl(var(--muted-foreground))"; }}
        >
          <ArrowLeft style={{ width: "14px", height: "14px" }} />
          Back to Home
        </button>
      </nav>
      <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", padding: "24px" }}>
      <div style={{ width: "100%", maxWidth: "340px" }}>
        <div style={{ textAlign: "center", marginBottom: "32px" }}>
          <div style={{ display: "flex", justifyContent: "center", marginBottom: "12px" }}>
            <LogoMark size={36} />
          </div>
          <p style={{ fontSize: "12px", color: "hsl(var(--muted-foreground))", marginTop: "6px" }}>
            Admin access
          </p>
        </div>

        <form onSubmit={handleSubmit}>
          <div style={{ animation: shake ? "shake 0.4s ease" : "none" }}>
            <input
              type="password"
              value={password}
              onChange={(e) => {
                setPassword(e.target.value);
                setError(null);
              }}
              placeholder="Enter password"
              autoFocus
              disabled={loading}
              style={{
                width: "100%",
                padding: "10px 14px",
                fontSize: "14px",
                border: `1px solid ${error ? "hsl(var(--destructive))" : "hsl(var(--border))"}`,
                borderRadius: "8px",
                background: "hsl(var(--background))",
                color: "hsl(var(--foreground))",
                outline: "none",
                boxSizing: "border-box",
                marginBottom: "8px",
                transition: "border-color 0.15s",
                opacity: loading ? 0.6 : 1,
              }}
            />
            {error && (
              <p
                style={{
                  fontSize: "12px",
                  color: "hsl(var(--destructive))",
                  marginBottom: "8px",
                  textAlign: "center",
                }}
              >
                {error}
              </p>
            )}
            <button
              type="submit"
              disabled={!password || loading}
              style={{
                width: "100%",
                padding: "10px",
                fontSize: "14px",
                fontWeight: 500,
                background: "hsl(var(--primary))",
                color: "hsl(var(--primary-foreground))",
                border: "none",
                borderRadius: "8px",
                cursor: password && !loading ? "pointer" : "not-allowed",
                opacity: password && !loading ? 1 : 0.5,
                transition: "opacity 0.15s",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "8px",
              }}
            >
              {loading && (
                <Loader2 style={{ width: "14px", height: "14px", animation: "spin 1s linear infinite" }} />
              )}
              {loading ? "Verifying..." : "Continue"}
            </button>
          </div>
        </form>
      </div>
      </div>

      <style>{`
        @keyframes shake {
          0%, 100% { transform: translateX(0); }
          20% { transform: translateX(-6px); }
          40% { transform: translateX(6px); }
          60% { transform: translateX(-4px); }
          80% { transform: translateX(4px); }
        }
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>

      <QuoteCarousel />
    </div>
  );
}
